// What a multi-selection has in common (DOM-free, unit-tested).
//
// Each selected object describes the properties its own panel edits as a list of
// `PropField`s. Two objects share a property when their fields carry the same
// `key` (what the property IS — a polygon's outline and an arc's stroke are both
// `stroke`), so the panel shows exactly the INTERSECTION: a field every selected
// object has, of one kind. Its value is shown when they all agree and is `null`
// ("mixed") when they don't; writing it sets every object.

export type PropKind =
  | 'color'
  | 'number'
  | 'opacity'
  | 'select'
  | 'text';

export interface PropField {
  key: string;
  /** English section title; the panel groups by it. */
  section: string;
  /** English label. */
  label: string;
  kind: PropKind;
  get(): number | string;
  /** Write the value; may return a message for the command bar (why it was
   *  clamped), READY TO SHOW — translated, its placeholders filled — or nothing. */
  set(v: number | string): string | null | void;
  /** Why this object cannot take `v`, as an English KEY (the panel translates
   *  it, and may set it inside a sentence of its own), or null when it can. A
   *  refused object is left as it was. */
  refuses?(v: number | string): string | null;
  /** The sentence that says how many objects refused, an English KEY with
   *  `{n}` (how many) and `{reason}` (the first refusal); absent, the reason
   *  alone is shown. */
  refusedMsg?: string;
  /** True while this one object disagrees with itself (a room whose walls have
   *  different setbacks): the field reads mixed whatever `get` returns. */
  mixed?(): boolean;
  /** A select's `[value, English label]` pairs. */
  options?: ReadonlyArray<readonly [string, string]>;
}

export interface CommonField {
  key: string;
  section: string;
  label: string;
  kind: PropKind;
  options?: ReadonlyArray<readonly [string, string]>;
  /** The shared value, or null when the selection disagrees. */
  value: number | string | null;
  /** One field per selected object, in selection order. */
  fields: PropField[];
}

function same(kind: PropKind, a: number | string, b: number | string): boolean {
  if (kind === 'number' || kind === 'opacity') {
    const x = Number(a), y = Number(b);
    if (Number.isFinite(x) && Number.isFinite(y)) return Math.abs(x - y) < 0.005;
  }
  if (kind === 'color') return String(a).toLowerCase() === String(b).toLowerCase();
  return String(a) === String(b);
}

const optionsKey = (o?: ReadonlyArray<readonly [string, string]>): string => JSON.stringify(o ?? []);

/** The value every object of a shared field has NOW, or null when they disagree
 *  (read again after a write, which may change another field's objects too). */
export function commonValue(field: Pick<CommonField, 'kind' | 'fields'>): number | string | null {
  const v0 = field.fields[0]?.get();
  if (v0 === undefined || field.fields.some((f) => f.mixed?.())) return null;
  return field.fields.every((f) => same(field.kind, f.get(), v0)) ? v0 : null;
}

/** The properties every list has, matched by key and kind (a select also by its
 *  options), ordered by the first list and then grouped by section in the order
 *  the sections first appear. An empty input has nothing in common. */
export function commonFields(lists: PropField[][]): CommonField[] {
  if (!lists.length) return [];
  const out: CommonField[] = [];
  for (const head of lists[0]) {
    if (out.some((c) => c.key === head.key)) continue;
    const fields: PropField[] = [];
    for (const list of lists) {
      const f = list.find((x) => x.key === head.key);
      if (!f || f.kind !== head.kind || (head.kind === 'select' && optionsKey(f.options) !== optionsKey(head.options))) break;
      fields.push(f);
    }
    if (fields.length !== lists.length) continue;
    const field: CommonField = { key: head.key, section: head.section, label: head.label, kind: head.kind, options: head.options, value: null, fields };
    field.value = commonValue(field);
    out.push(field);
  }
  const sections: string[] = [];
  for (const c of out) if (!sections.includes(c.section)) sections.push(c.section);
  return sections.flatMap((s) => out.filter((c) => c.section === s));
}

/** What writing a shared field did. */
export interface CommonResult {
  /** How many objects refused the value and were left as they were. */
  refused: number;
  /** The messages the accepting objects' setters returned, in selection order,
   *  ready to show. */
  reasons: string[];
  /** The first refused object's reason (an English key), or null when none refused. */
  refusal: string | null;
}

/** Write one value to every object that shares the field and can take it. */
export function applyCommon(field: CommonField, v: number | string): CommonResult {
  const out: CommonResult = { refused: 0, reasons: [], refusal: null };
  for (const f of field.fields) {
    const why = f.refuses?.(v) ?? null;
    if (why) { out.refused++; out.refusal ??= why; continue; }
    const msg = f.set(v);
    if (typeof msg === 'string' && msg) out.reasons.push(msg);
  }
  return out;
}
